import { Injectable, computed, signal } from '@angular/core';
import { TaskCard, TaskStatus } from '../../../../shared/definitions/task-card.model';
import type {
  RecurringTaskTemplate,
  RecurringTaskExecution,
  RecurringTaskSchedule,
  SeasonalRule
} from '@shared/definitions/recurring-task.model';
import type { IRecurringTasksStore } from './recurring-tasks-store.interface';

/**
 * In-memory implementation of the recurring-tasks store.
 *
 * Despite the "firestore" in its name, this store never actually persists to
 * Firestore — the legacy `RecurringTaskService` was seeded in-memory too and
 * no migration to Firestore was ever built. Templates, executions, and
 * schedules live only for the life of the process. Replaces the legacy
 * `RecurringTaskService` retired as part of Phase C (2026-04-20).
 *
 * **Kept the "Firestore" name** so the `?store=mock|firestore` URL toggle and
 * existing consumers keep working unchanged; renaming would churn call sites
 * for no behavioural win. Actual Firestore persistence is future work.
 */
@Injectable({ providedIn: 'root' })
export class RecurringTasksFirestoreStore implements IRecurringTasksStore {
  private readonly _templates = signal<readonly RecurringTaskTemplate[]>([]);
  private readonly _executions = signal<readonly RecurringTaskExecution[]>([]);
  private readonly _schedules = signal<readonly RecurringTaskSchedule[]>([]);
  private readonly _loading = signal(false);
  private readonly _lastError = signal<Error | null>(null);

  readonly templates = this._templates.asReadonly();
  readonly executions = this._executions.asReadonly();
  readonly schedules = this._schedules.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly lastError = this._lastError.asReadonly();

  readonly activeTemplates = computed(() => this._templates().filter(t => t.isActive));
  readonly totalTemplates = computed(() => this._templates().length);

  constructor() {
    // Seed with default maintenance templates (HVAC filter, annual service) —
    // matches the legacy service's initial state so existing UI renders the
    // same defaults.
    this._templates.set(this.buildDefaultTemplates());
    this.recomputeSchedules();
  }

  async refresh(): Promise<void> {
    // In-memory only today; nothing to re-fetch.
    return;
  }

  async createTemplate(
    template: Omit<RecurringTaskTemplate, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<RecurringTaskTemplate> {
    try {
      const newTemplate: RecurringTaskTemplate = {
        ...template,
        id: this.generateId(),
        createdAt: new Date(),
        updatedAt: new Date()
      };
      this._templates.set([...this._templates(), newTemplate]);
      this.recomputeSchedules();
      return newTemplate;
    } catch (err) {
      this._lastError.set(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  }

  async updateTemplate(templateId: string, updates: Partial<RecurringTaskTemplate>): Promise<void> {
    try {
      const list = this._templates().slice();
      const idx = list.findIndex(t => t.id === templateId);
      if (idx === -1) throw new Error(`Template ${templateId} not found`);
      list[idx] = { ...list[idx], ...updates, updatedAt: new Date() };
      this._templates.set(list);
      this.recomputeSchedules();
    } catch (err) {
      this._lastError.set(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  }

  async deleteTemplate(templateId: string): Promise<void> {
    try {
      this._templates.set(this._templates().filter(t => t.id !== templateId));
      this.recomputeSchedules();
    } catch (err) {
      this._lastError.set(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  }

  getTemplateById(templateId: string): RecurringTaskTemplate | null {
    return this._templates().find(t => t.id === templateId) ?? null;
  }

  getTemplatesForProperty(propertyUID: string): readonly RecurringTaskTemplate[] {
    return this._templates().filter(t => t.propertyUIDs?.includes(propertyUID));
  }

  getDueExecutions(): readonly RecurringTaskSchedule[] {
    const now = Date.now();
    return this._schedules().filter(s => new Date(s.nextExecutionDate).getTime() <= now);
  }

  getUpcomingExecutions(daysAhead = 30): readonly RecurringTaskSchedule[] {
    const now = Date.now();
    const cutoff = now + daysAhead * 86_400_000;
    return this._schedules().filter(s => {
      const t = new Date(s.nextExecutionDate).getTime();
      return t > now && t <= cutoff;
    });
  }

  async createTaskFromTemplate(
    template: RecurringTaskTemplate,
    propertyUID: string,
    scheduledDate: Date,
    propertyName?: string
  ): Promise<TaskCard> {
    try {
      const dueDate = new Date(scheduledDate);
      if (template.leadTime) dueDate.setDate(dueDate.getDate() + template.leadTime);

      const task: TaskCard = {
        id: this.generateId(),
        title: `${template.name} - ${propertyName || 'Property'}`,
        description: `${template.description}\n\n[Auto-generated from recurring template: ${template.name}]`,
        type: template.type,
        status: TaskStatus.PLANNED,
        priority: template.priority,
        propertyUID,
        propertyName: propertyName || '',
        propertyAddress: '',
        scheduledDate,
        dueDate,
        estimatedDuration: template.estimatedDuration,
        cost: template.estimatedCost,
        specialties: template.specialties,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const execution: RecurringTaskExecution = {
        id: this.generateId(),
        templateId: template.id,
        taskId: task.id,
        propertyUID,
        scheduledDate,
        dueDate,
        status: 'created',
        createdAt: new Date()
      };
      this._executions.set([...this._executions(), execution]);
      return task;
    } catch (err) {
      this._lastError.set(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  }

  async skipExecution(executionId: string, reason: string): Promise<void> {
    try {
      const list = this._executions().slice();
      const idx = list.findIndex(e => e.id === executionId);
      if (idx === -1) return;
      list[idx] = { ...list[idx], status: 'skipped', skipReason: reason };
      this._executions.set(list);
    } catch (err) {
      this._lastError.set(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  }

  /**
   * Kept for API compatibility with the legacy `Observable`-returning getter.
   * Resolves with the current snapshot — callers should prefer `getTemplateById`.
   */
  async getTemplate(templateId: string): Promise<RecurringTaskTemplate | undefined> {
    return this.getTemplateById(templateId) ?? undefined;
  }

  // --- internals ---

  /**
   * Calculate the next execution date for a template, applying frequency,
   * time-of-month/day-of-week preferences, weekend/holiday skipping, and
   * seasonal adjustments in order.
   */
  private calculateNextExecutionDate(template: RecurringTaskTemplate, lastExecution?: Date): Date {
    const baseDate = lastExecution || new Date();
    let nextDate = new Date(baseDate);

    switch (template.frequency) {
      case 'weekly':
        nextDate.setDate(nextDate.getDate() + 7 * template.interval);
        break;
      case 'monthly':
        nextDate.setMonth(nextDate.getMonth() + template.interval);
        break;
      case 'quarterly':
        nextDate.setMonth(nextDate.getMonth() + 3 * template.interval);
        break;
      case 'yearly':
        nextDate.setFullYear(nextDate.getFullYear() + template.interval);
        break;
      case 'custom':
        if (template.customFrequencyDays) {
          nextDate.setDate(nextDate.getDate() + template.customFrequencyDays);
        }
        break;
    }

    if (template.preferredTimeOfMonth && template.frequency !== 'weekly') {
      nextDate = this.adjustForTimeOfMonth(nextDate, template.preferredTimeOfMonth);
    }
    if (template.preferredDayOfWeek !== undefined) {
      nextDate = this.adjustForDayOfWeek(nextDate, template.preferredDayOfWeek);
    }
    if (template.skipWeekends) {
      nextDate = this.skipWeekendsAndHolidays(nextDate, template.skipHolidays);
    }
    if (template.seasonalAdjustment && template.seasonalRules) {
      nextDate = this.applySeasonalAdjustments(nextDate, template.seasonalRules);
    }

    return nextDate;
  }

  private calculateUpcomingExecutions(template: RecurringTaskTemplate, startDate?: Date): Date[] {
    const executions: Date[] = [];
    let currentDate = startDate || new Date();
    const endDate = new Date();
    endDate.setFullYear(endDate.getFullYear() + 1);

    for (let i = 0; i < 24 && currentDate <= endDate; i++) {
      currentDate = this.calculateNextExecutionDate(template, i === 0 ? undefined : currentDate);
      if (currentDate <= endDate) executions.push(new Date(currentDate));
    }
    return executions;
  }

  private recomputeSchedules(): void {
    const templates = this._templates().filter(t => t.isActive);
    const schedules: RecurringTaskSchedule[] = [];

    for (const template of templates) {
      for (const propertyUID of template.propertyUIDs) {
        const lastExecution = this.getLastExecutionForProperty(template.id, propertyUID);
        const nextExecutionDate = this.calculateNextExecutionDate(template, lastExecution?.completedAt);
        const upcomingExecutions = this.calculateUpcomingExecutions(template, nextExecutionDate);
        schedules.push({
          templateId: template.id,
          propertyUID,
          nextExecutionDate,
          upcomingExecutions,
          lastExecution
        });
      }
    }
    this._schedules.set(schedules);
  }

  private getLastExecutionForProperty(
    templateId: string,
    propertyUID: string
  ): RecurringTaskExecution | undefined {
    return this._executions()
      .filter(e => e.templateId === templateId && e.propertyUID === propertyUID && e.status === 'completed')
      .sort((a, b) => (b.completedAt?.getTime() || 0) - (a.completedAt?.getTime() || 0))[0];
  }

  private adjustForTimeOfMonth(date: Date, timeOfMonth: 'beginning' | 'middle' | 'end'): Date {
    const newDate = new Date(date);
    const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    switch (timeOfMonth) {
      case 'beginning': newDate.setDate(Math.min(5, daysInMonth)); break;
      case 'middle': newDate.setDate(Math.min(15, daysInMonth)); break;
      case 'end': newDate.setDate(Math.max(daysInMonth - 5, 1)); break;
    }
    return newDate;
  }

  private adjustForDayOfWeek(date: Date, preferredDay: number): Date {
    const newDate = new Date(date);
    const currentDay = newDate.getDay();
    const daysToAdd = (preferredDay - currentDay + 7) % 7;
    if (daysToAdd === 0 && newDate.getTime() <= Date.now()) {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() + daysToAdd);
    }
    return newDate;
  }

  private skipWeekendsAndHolidays(date: Date, skipHolidays: boolean): Date {
    const newDate = new Date(date);
    while (newDate.getDay() === 0 || newDate.getDay() === 6) {
      newDate.setDate(newDate.getDate() + 1);
    }
    if (skipHolidays) {
      const holidays = this.getHolidays(newDate.getFullYear());
      while (holidays.some(h => this.isSameDay(h, newDate))) {
        newDate.setDate(newDate.getDate() + 1);
        while (newDate.getDay() === 0 || newDate.getDay() === 6) {
          newDate.setDate(newDate.getDate() + 1);
        }
      }
    }
    return newDate;
  }

  private applySeasonalAdjustments(date: Date, seasonalRules: SeasonalRule[]): Date {
    const season = this.getSeason(date);
    const applicableRule = seasonalRules.find(r => r.season === season);
    if (!applicableRule) return date;
    const newDate = new Date(date);
    if (applicableRule.adjustmentType === 'timing' && typeof applicableRule.adjustmentValue === 'number') {
      newDate.setDate(newDate.getDate() + applicableRule.adjustmentValue);
    }
    return newDate;
  }

  private getSeason(date: Date): 'spring' | 'summer' | 'fall' | 'winter' {
    const month = date.getMonth();
    if (month >= 2 && month <= 4) return 'spring';
    if (month >= 5 && month <= 7) return 'summer';
    if (month >= 8 && month <= 10) return 'fall';
    return 'winter';
  }

  private getHolidays(year: number): Date[] {
    return [
      new Date(year, 0, 1),   // New Year's Day
      new Date(year, 6, 4),   // Independence Day
      new Date(year, 11, 25)  // Christmas Day
    ];
  }

  private isSameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear()
      && a.getMonth() === b.getMonth()
      && a.getDate() === b.getDate();
  }

  private generateId(): string {
    return 'rec_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  private buildDefaultTemplates(): RecurringTaskTemplate[] {
    const now = new Date();
    return [
      {
        id: 'hvac-filter-monthly',
        name: 'HVAC Filter Replacement',
        description: 'Replace HVAC air filters to maintain air quality and system efficiency',
        type: 'maintenance',
        frequency: 'monthly',
        interval: 1,
        seasonalAdjustment: true,
        seasonalRules: [
          { season: 'summer', adjustmentType: 'frequency', adjustmentValue: 0.75, description: 'Increase frequency during high-use summer months' },
          { season: 'winter', adjustmentType: 'frequency', adjustmentValue: 0.75, description: 'Increase frequency during high-use winter months' }
        ],
        priority: 'medium',
        estimatedDuration: 0.5,
        estimatedCost: 25,
        specialties: ['HVAC', 'General Maintenance'],
        autoAssign: true,
        skipWeekends: false,
        skipHolidays: false,
        preferredTimeOfMonth: 'beginning',
        leadTime: 3,
        propertyUIDs: [],
        isActive: true,
        createdAt: now,
        updatedAt: now,
        createdBy: 'system'
      },
      {
        id: 'annual-hvac-service',
        name: 'Annual HVAC System Service',
        description: 'Comprehensive annual HVAC system inspection and maintenance',
        type: 'maintenance',
        frequency: 'yearly',
        interval: 1,
        seasonalAdjustment: true,
        seasonalRules: [
          { season: 'spring', adjustmentType: 'timing', adjustmentValue: 0, description: 'Schedule in spring before heavy summer use' }
        ],
        priority: 'high',
        estimatedDuration: 4,
        estimatedCost: 200,
        specialties: ['HVAC'],
        autoAssign: true,
        skipWeekends: true,
        skipHolidays: true,
        preferredTimeOfMonth: 'middle',
        leadTime: 14,
        propertyUIDs: [],
        isActive: true,
        createdAt: now,
        updatedAt: now,
        createdBy: 'system'
      }
    ];
  }
}
